import { useMeasure } from '@uidotdev/usehooks';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';

import { useTheme } from '@mui/material/styles';

import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';

import {
  useBreakpoints,
  useFormatters,
  usePostEnergyUsageByHour,
} from '@/hooks';

import {
  EnergyUsageDataKey,
  getEnergyUsageInputFromFilters,
  store,
} from '../store';

export const DailyAverageChartContainerHeight = 300;

function DailyAverageChart() {
  const { t } = useTranslation();
  const theme = useTheme();
  const snap = useSnapshot(store);
  const { isMobileDevice } = useBreakpoints();

  // used to adjust centering of element in the chart area (loading, message, ...)
  const [titleRef, { height: titleHeight }] = useMeasure();

  // generic number format
  const { formatFloat } = useFormatters();

  const params = getEnergyUsageInputFromFilters(snap as typeof store);
  const [energyUsageByHourResult, refresh] = usePostEnergyUsageByHour(params);

  const dataset = energyUsageByHourResult.data
    ? // convert 'hour' to string
      energyUsageByHourResult.data.map((d) => ({
        ...d,
        hour: (d.hour + '').padStart(2, '0'),
      }))
    : [];

  const dataKey = snap.viewModeDataKey as EnergyUsageDataKey;

  const allZeros = !dataset.length || dataset.every((d) => !d[dataKey]);

  const isLoading = energyUsageByHourResult.isLoading;

  return (
    <Paper
      variant="elevation"
      sx={{
        height: `${DailyAverageChartContainerHeight}px`,
        width: '100%',
        borderRadius: 4,
      }}
    >
      <Stack
        ref={titleRef}
        direction="row"
        alignItems="flex-start"
        flexGrow={1}
        spacing={1}
        sx={{ pl: 3, pt: 2 }}
      >
        <Typography variant="h5">Daily average</Typography>
        <Typography variant="h5" color="GrayText">
          {snap.viewModeUnit}
        </Typography>
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{
          width: '100%',
          height: DailyAverageChartContainerHeight - (titleHeight || 0),
        }}
      >
        {isLoading && <CircularProgress />}

        {!isLoading && allZeros && (
          <Typography variant="overline">{t('noData')}</Typography>
        )}

        {!isLoading && !allZeros && (
          <ResponsiveChartContainer
            dataset={dataset}
            // margin={{ top: 60, bottom: 30, right: 20 }}
            margin={{
              top: 30,
              left: isMobileDevice ? 40 : 50,
              right: isMobileDevice ? 20 : 30,
            }}
            series={[
              {
                type: 'bar',
                dataKey,
                color: snap.isAveragePowerMode
                  ? theme.palette.primary.main
                  : theme.palette.secondary.main,
                valueFormatter: (value) => {
                  return value !== null
                    ? `${formatFloat(value)} ${snap.viewModeUnit}`
                    : '';
                },
              },
            ]}
            xAxis={[
              {
                dataKey: 'hour',
                scaleType: 'band',
                id: 'x-axis-id',
                valueFormatter: (value, context) => {
                  return context.location === 'tick' ? value : `${value}:00`;
                },
              },
            ]}
          >
            <BarPlot
              slotProps={{
                bar: {
                  clipPath: !isMobileDevice
                    ? `inset(0px 3px 0px 3px round 2px 2px 0px 0px)`
                    : `inset(0px 0px 0px 0px round 2px 2px 0px 0px)`,
                },
              }}
            />
            <ChartsXAxis position="bottom" axisId="x-axis-id" />
            <ChartsYAxis position="left" />
            <ChartsTooltip />
          </ResponsiveChartContainer>
        )}
      </Stack>
    </Paper>
  );
}
export { DailyAverageChart };
